// ─────────────────────────────────────────────────────────────────────────
// freshness.ts — the staleness audit (brief v1 §9, plan §C/§D).
//
// Time-sensitive figures (rates, FiT, STC price, battery taper, emissions) go
// out of date on the AU regulatory calendar. This makes "what is stale, where
// did it come from, when to recheck" machine-readable, so a build gate and a
// visible "current as of" badge can hang off it.
// ─────────────────────────────────────────────────────────────────────────

import type { Confidence, Sourced } from './types.js';

/** A Sourced value paired with a human-readable path for reporting. */
export interface LabelledSourced {
  label: string;
  value: Sourced<unknown>;
}

export interface FreshnessFinding {
  label: string;
  source: string;
  url: string;
  reviewDue: string;
  lastUpdated: string;
  confidence: Confidence;
  /** Whole days past reviewDue (negative = still in date). */
  daysOverdue: number;
}

export interface FreshnessReport {
  /** Values whose reviewDue is on/before `today` — these fail a live build. */
  stale: FreshnessFinding[];
  /** Values with confidence !== 'verified' — always a warning, never a hard fail. */
  unverified: FreshnessFinding[];
  /** The most recent `lastUpdated` across all values → the "current as of" date. */
  maxLastUpdated: string | null;
  ok: boolean; // true when nothing is stale
}

const MS_PER_DAY = 86_400_000;

function daysBetween(fromISO: string, toISO: string): number {
  const a = Date.parse(fromISO);
  const b = Date.parse(toISO);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / MS_PER_DAY);
}

/**
 * Audit a list of sourced values against `today` (ISO YYYY-MM-DD).
 * A value is STALE when reviewDue <= today; UNVERIFIED when confidence is not
 * 'verified'. (The two sets overlap freely.)
 */
export function auditFreshness(
  values: LabelledSourced[],
  today: string,
): FreshnessReport {
  const stale: FreshnessFinding[] = [];
  const unverified: FreshnessFinding[] = [];
  let maxLastUpdated: string | null = null;

  for (const { label, value } of values) {
    if (!maxLastUpdated || value.lastUpdated > maxLastUpdated) {
      maxLastUpdated = value.lastUpdated;
    }
    const daysOverdue = daysBetween(value.reviewDue, today);
    const finding: FreshnessFinding = {
      label,
      source: value.source,
      url: value.url,
      reviewDue: value.reviewDue,
      lastUpdated: value.lastUpdated,
      confidence: value.confidence,
      daysOverdue,
    };
    if (daysOverdue >= 0) stale.push(finding);
    if (value.confidence !== 'verified') unverified.push(finding);
  }

  return {
    stale,
    unverified,
    maxLastUpdated,
    ok: stale.length === 0,
  };
}

/** Format an ISO date as a human "current as of" string, e.g. "1 May 2026". */
export function formatAsOf(iso: string | null): string {
  if (!iso) return 'unknown';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
